import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import siteConfig from "../config/siteConfig";

export const CartContext = createContext(null);

const readStorage = (key, fallback) => {
  if (typeof window === "undefined") return fallback;
  try {
    const storedValue = window.localStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : fallback;
  } catch {
    return fallback;
  }
};

const writeStorage = (key, value) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // The storefront remains usable when storage is unavailable.
  }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => readStorage(siteConfig.storage.cart, []));
  const [wishlist, setWishlist] = useState(() => readStorage(siteConfig.storage.wishlist, []));

  useEffect(() => writeStorage(siteConfig.storage.cart, cartItems), [cartItems]);
  useEffect(() => writeStorage(siteConfig.storage.wishlist, wishlist), [wishlist]);

  const addToCart = (productId, quantity = 1, size = "", color = "") => {
    setCartItems((currentItems) => {
      const matchingItem = currentItems.find(
        (item) => item.id === productId && item.size === size && item.color === color
      );

      if (matchingItem) {
        return currentItems.map((item) =>
          item.id === productId && item.size === size && item.color === color
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      return [...currentItems, { id: productId, size, color, quantity }];
    });
  };

  const removeFromCart = (productId, size, color) => {
    setCartItems((currentItems) =>
      currentItems.filter(
        (item) => !(item.id === productId && item.size === size && item.color === color)
      )
    );
  };

  const updateQuantity = (productId, size, color, quantity) => {
    if (quantity < 1) {
      removeFromCart(productId, size, color);
      return;
    }

    setCartItems((currentItems) =>
      currentItems.map((item) =>
        item.id === productId && item.size === size && item.color === color
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = useCallback(() => setCartItems([]), []);

  const addToWishlist = (productId) => {
    setWishlist((current) => (current.includes(productId) ? current : [...current, productId]));
  };

  const removeFromWishlist = (productId) => {
    setWishlist((current) => current.filter((id) => id !== productId));
  };

  const toggleWishlist = (productId) => {
    setWishlist((current) =>
      current.includes(productId)
        ? current.filter((id) => id !== productId)
        : [...current, productId]
    );
  };

  const cartCount = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems]
  );

  const value = {
    cartItems,
    wishlist,
    cartCount,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
